import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Components
import DropdownContent from "new_components/atoms/DropdownContent";
import Text from "new_components/atoms/Text";
import SearchBar from "new_components/molecules/SearchBar";

// Constants
import typography from "styles/typography";
import colors from "styles/colors";

const StyledListItem = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? colors.background.lightBlue : "transparent"};
  border-radius: 4px;

  &:hover {
    background-color: ${colors.background.lightBlue};
  }
`;

const HighlightedText = styled(Text)`
  ${({ highlight }) =>
    highlight
      ? `
    font-weight: ${typography.caption.semibold.fontWeight};
    font-size: ${typography.caption.semibold.fontSize};
    line-height: ${typography.caption.semibold.lineHeight};
  `
      : `
    font-weight: ${typography.caption.regular.fontWeight};
    font-size: ${typography.caption.regular.fontSize};
    line-height: ${typography.caption.regular.lineHeight};
  `}
`;

const SearchDropdown = ({
  data,
  onSearch,
  renderItem,
  placeholder,
  InputComponent,
  onSelect,
  maxScrollHeight = "200px",
  groupData,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Merge users and groups. Mark groups with a type property.
  const combinedData = [
    ...data.map((item) => ({ ...item, type: "user" })),
    ...((groupData || []).map((item) => ({ ...item, type: "group" }))),
  ];

  // Filter based on the search term
  const filteredData = combinedData.filter((item) => onSearch(item, searchTerm));
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setActiveIndex(-1); // Reset active index when the search changes
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Select the first available item if filteredData is not empty
      if (filteredData.length > 0) {
        onSelect(filteredData[activeIndex >= 0 ? activeIndex : 0]);
        setSearchTerm(""); // Clear search after selection
      }
    } else if (e.key === "ArrowDown") {
      // Navigate to the next item
      setActiveIndex((prev) =>
        prev < filteredData.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      // Navigate to the previous item
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }
  };

  // Render items differently if it’s a group
  const defaultRenderItem = (item) => {
    const parts = item.text.split(new RegExp(`(${searchTerm})`, "gi"));

    // If this is a group, style it differently (for example, italic with a distinct color)
    if (item.type === "group") {
      return (
        <HighlightedText style={{ fontStyle: "italic", color: colors.text.primary }}>
          {parts.map((part, index) => {
            const trimmedPart = part.trim();
            return trimmedPart.toLowerCase() === searchTerm.toLowerCase() ? (
              <HighlightedText key={index} highlight>
                {part}
              </HighlightedText>
            ) : (
              part
            );
          })}
        </HighlightedText>
      );
    }

    return (
      <HighlightedText>
        {parts.map((part, index) => {
          const trimmedPart = part.trim();
          return trimmedPart.toLowerCase() === searchTerm.toLowerCase() ? (
            <HighlightedText key={index} highlight>
              {part}
            </HighlightedText>
          ) : (
            part
          );
        })}
      </HighlightedText>
    );
  };

  const handleItemClick = (item) => {
    onSelect(item); // Trigger the onSelect callback with the selected item
    setSearchTerm(""); // Clear search after selection
  };

  return (
    <>
      {InputComponent ? (
        <InputComponent
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <SearchBar
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
      )}
      {searchTerm && filteredData.length > 0 && (
        <DropdownContent scrollable maxHeight={maxScrollHeight}>
          {/* Filtered Results */}
          {filteredData.map((item, index) => (
            <StyledListItem
              key={item.key}
              isActive={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleItemClick(item)}
            >
              {renderItem ? renderItem(item) : defaultRenderItem(item)}
            </StyledListItem>
          ))}
        </DropdownContent>
      )}
      {searchTerm && filteredData.length === 0 && (
        <DropdownContent>
          <Text color={colors.text.gray}>No results found</Text>
        </DropdownContent>
      )}
    </>
  );
};

SearchDropdown.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Unique identifier for the item
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired, // Value of the selected item
      text: PropTypes.string.isRequired, // Text displayed in the dropdown
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Another identifier
    })
  ).isRequired, // The data to search
  onSearch: PropTypes.func.isRequired, // Function to filter data based on the search term
  renderItem: PropTypes.func, // Optional function to render each dropdown item
  placeholder: PropTypes.string, // Placeholder for the search input
  InputComponent: PropTypes.func, // Custom input component
  onSelect: PropTypes.func.isRequired, // Callback triggered when an item is selected
  maxScrollHeight: PropTypes.string, // Maximum height of the dropdown content
};

// PulseBuilder-styled SearchDropdown variant
const PulseBuilderStyledSearchDropdown = ({ style = "default", ...props }) => {
  if (style === "pulsebuilder") {
    return <PulseBuilderSearchDropdown {...props} />;
  }
  return <SearchDropdown {...props} />;
};

const PulseBuilderSearchDropdown = ({
  data,
  onSearch,
  renderItem,
  placeholder,
  InputComponent,
  onSelect,
  maxScrollHeight = "200px",
  groupData,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Merge users and groups. Mark groups with a type property.
  const combinedData = [
    ...data.map((item) => ({ ...item, type: "user" })),
    ...((groupData || []).map((item) => ({ ...item, type: "group" }))),
  ];

  // Filter based on the search term
  const filteredData = combinedData.filter((item) => onSearch(item, searchTerm));
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setActiveIndex(-1); // Reset active index when the search changes
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Select the first available item if filteredData is not empty
      if (filteredData.length > 0) {
        onSelect(filteredData[activeIndex >= 0 ? activeIndex : 0]);
        setSearchTerm(""); // Clear search after selection
      }
    } else if (e.key === "ArrowDown") {
      // Navigate to the next item
      setActiveIndex((prev) =>
        prev < filteredData.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      // Navigate to the previous item
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }
  };

  // Render items differently if it's a group
  const defaultRenderItem = (item) => {
    const parts = item.text.split(new RegExp(`(${searchTerm})`, "gi"));

    // If this is a group, style it differently (for example, italic with a distinct color)
    if (item.type === "group") {
      return (
        <PulseBuilderHighlightedText style={{ fontStyle: "italic", color: "#414141" }}>
          {parts.map((part, index) => {
            const trimmedPart = part.trim();
            return trimmedPart.toLowerCase() === searchTerm.toLowerCase() ? (
              <PulseBuilderHighlightedText key={index} highlight>
                {part}
              </PulseBuilderHighlightedText>
            ) : (
              part
            );
          })}
        </PulseBuilderHighlightedText>
      );
    }

    return (
      <PulseBuilderHighlightedText>
        {parts.map((part, index) => {
          const trimmedPart = part.trim();
          return trimmedPart.toLowerCase() === searchTerm.toLowerCase() ? (
            <PulseBuilderHighlightedText key={index} highlight>
              {part}
            </PulseBuilderHighlightedText>
          ) : (
            part
          );
        })}
      </PulseBuilderHighlightedText>
    );
  };

  const handleItemClick = (item) => {
    onSelect(item); // Trigger the onSelect callback with the selected item
    setSearchTerm(""); // Clear search after selection
  };

  return (
    <PulseBuilderSearchContainer>
      {InputComponent ? (
        <InputComponent
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <PulseBuilderSearchInput
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
      )}
      {searchTerm && filteredData.length > 0 && (
        <PulseBuilderDropdownContent maxHeight={maxScrollHeight}>
          {/* Filtered Results */}
          {filteredData.map((item, index) => (
            <PulseBuilderListItem
              key={item.key}
              isActive={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleItemClick(item)}
            >
              {renderItem ? renderItem(item) : defaultRenderItem(item)}
            </PulseBuilderListItem>
          ))}
        </PulseBuilderDropdownContent>
      )}
      {searchTerm && filteredData.length === 0 && (
        <PulseBuilderDropdownContent>
          <PulseBuilderNoResults>No results found</PulseBuilderNoResults>
        </PulseBuilderDropdownContent>
      )}
    </PulseBuilderSearchContainer>
  );
};

// PulseBuilder-styled components
const PulseBuilderSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PulseBuilderSearchInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const PulseBuilderDropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: ${props => props.maxHeight || "200px"};
  overflow-y: auto;
`;

const PulseBuilderListItem = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? "#f0f4ff" : "transparent"};
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f4ff;
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const PulseBuilderHighlightedText = styled.span`
  font-family: "Poppins", sans-serif;
  ${({ highlight }) =>
    highlight
      ? `
    font-weight: 600;
    font-size: 14px;
    color: #476dfa;
  `
      : `
    font-weight: 400;
    font-size: 14px;
    color: #414141;
  `}
`;

const PulseBuilderNoResults = styled.div`
  padding: 12px 14px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #7e7e7e;
  text-align: center;
`;

PulseBuilderStyledSearchDropdown.propTypes = {
  style: PropTypes.oneOf(["default", "pulsebuilder"]),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  onSearch: PropTypes.func.isRequired,
  renderItem: PropTypes.func,
  placeholder: PropTypes.string,
  InputComponent: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  maxScrollHeight: PropTypes.string,
  groupData: PropTypes.array,
};

export default SearchDropdown;
export { PulseBuilderStyledSearchDropdown };
